import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Users, Package, FileCheck, DollarSign, Shield, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function HowItWorks() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-slate-900 mb-6">{t('how.title')}</h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            {t('how.subtitle')}
          </p>
        </div>

        {/* User Roles */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">User Roles & Registration</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              {
                role: 'Miner',
                icon: Package,
                description: 'Local artisanal & licensed miners',
                requirements: 'Mining license, TIN, KYC verification'
              },
              {
                role: 'Seller/Dealer',
                icon: TrendingUp,
                description: 'Licensed mineral exporters',
                requirements: 'Export permit, business license, bank details'
              },
              {
                role: 'Buyer',
                icon: DollarSign,
                description: 'Local & international buyers',
                requirements: 'Company registration, payment verification'
              },
              {
                role: 'Broker/Agent',
                icon: Users,
                description: 'Licensed trading intermediaries',
                requirements: 'Broker license, commission agreement'
              },
              {
                role: 'Verifier/Inspector',
                icon: FileCheck,
                description: 'Quality & document inspectors',
                requirements: 'Accredited lab certification'
              },
              {
                role: 'Admin',
                icon: Shield,
                description: 'Platform oversight & compliance',
                requirements: 'Government authorization'
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-sm border border-slate-200 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-amber-100 rounded-sm flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{item.role}</h3>
                <p className="text-slate-600 mb-3">{item.description}</p>
                <p className="text-sm text-slate-500">
                  <strong>Required:</strong> {item.requirements}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-sm p-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Account Creation Process</h3>
            <ul className="space-y-3 text-slate-700">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                <span><strong>Individual & Company Registration:</strong> Choose your account type based on your business structure</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                <span><strong>KYC & Business Verification:</strong> Upload government-issued ID, business registration documents</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                <span><strong>Upload Licenses:</strong> Mining license, export permit, TIN, tax clearance certificate</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                <span><strong>Country & Jurisdiction:</strong> Select Tanzania or international location for compliance</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                <span><strong>Payment Setup:</strong> Link mobile money, bank account, or international payment methods</span>
              </li>
            </ul>
          </div>
        </section>

        {/* For Miners/Sellers */}
        <section className="mb-20">
          <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-sm p-12 mb-8">
            <div className="flex items-center mb-4">
              <Package className="w-10 h-10 mr-4" />
              <h2 className="text-4xl font-bold">For Miners & Sellers</h2>
            </div>
            <p className="text-xl text-amber-50">List your minerals and connect with verified international buyers</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-sm border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
                <span className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">1</span>
                Create Listing
              </h3>
              <ul className="space-y-4 text-slate-700">
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Select mineral type (Tantalite, Gold, Coltan, Lithium, Copper, etc.)</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Enter quantity, grade, and packaging details</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Upload photos, videos with timestamps</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Specify location (mine, warehouse, bonded store)</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Upload assay reports & lab certificates</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-sm border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
                <span className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">2</span>
                Set Your Terms
              </h3>
              <ul className="space-y-4 text-slate-700">
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Define where transaction occurs (your warehouse/buyer's warehouse)</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>State control terms ("Consignment under seller control until full payment")</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Choose delivery terms: EXW, FOB, CIF, DAP</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Specify inspection & sampling conditions</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Select payment method: Advance, On-delivery, Post-assay</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-sm border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
                <span className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">3</span>
                Document Verification
              </h3>
              <ul className="space-y-4 text-slate-700">
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Upload certificate of origin</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Submit export permits & packing lists</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Admin reviews and approves documents</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Listing goes live for verified buyers</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-sm border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
                <span className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">4</span>
                Receive Offers & Close Deal
              </h3>
              <ul className="space-y-4 text-slate-700">
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Get instant email notifications for offers</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Negotiate through secure messaging</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Accept offer & coordinate inspection</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Payment held in escrow until delivery confirmed</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                  <span>Platform deducts 5% commission, you receive 95%</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* For Buyers */}
        <section className="mb-20">
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-sm p-12 mb-8">
            <div className="flex items-center mb-4">
              <DollarSign className="w-10 h-10 mr-4" />
              <h2 className="text-4xl font-bold">For Buyers</h2>
            </div>
            <p className="text-xl text-blue-50">Source verified minerals from Tanzanian suppliers with confidence</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-sm border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">1</span>
                Browse & Filter
              </h3>
              <ul className="space-y-4 text-slate-700">
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>Search by mineral type, grade, quantity</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>Filter by location, price, seller rating</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>View assay reports & certificates</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>Check seller verification status</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-sm border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">2</span>
                Due Diligence
              </h3>
              <ul className="space-y-4 text-slate-700">
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>Review seller's business terms</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>Request additional documentation</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>Arrange third-party inspection</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>Verify origin & quality certificates</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-sm border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">3</span>
                Make Offer & Pay
              </h3>
              <ul className="space-y-4 text-slate-700">
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>Submit offer with your price & terms</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>Negotiate through secure chat</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>Choose payment: SWIFT, Escrow, LC, Mobile Money</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>Payment held securely until goods delivered</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-sm border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">4</span>
                Receive & Confirm
              </h3>
              <ul className="space-y-4 text-slate-700">
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>Coordinate delivery & logistics</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>Inspect goods upon arrival</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>Confirm delivery on platform</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <span>Funds released to seller automatically</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* For Brokers */}
        <section className="mb-20">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-sm p-12 mb-8">
            <div className="flex items-center mb-4">
              <Users className="w-10 h-10 mr-4" />
              <h2 className="text-4xl font-bold">For Brokers & Agents</h2>
            </div>
            <p className="text-xl text-purple-50">Facilitate deals and earn commission for connecting buyers and sellers</p>
          </div>

          <div className="bg-white p-8 rounded-sm border border-slate-200">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Commission Structure</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-purple-50 rounded-sm">
                <p className="text-4xl font-bold text-purple-600 mb-2">2-3%</p>
                <p className="text-slate-700">Broker Commission</p>
                <p className="text-sm text-slate-600 mt-2">On successful transaction value</p>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-sm">
                <p className="text-4xl font-bold text-purple-600 mb-2">24hrs</p>
                <p className="text-slate-700">Payment Processing</p>
                <p className="text-sm text-slate-600 mt-2">After transaction confirmation</p>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-sm">
                <p className="text-4xl font-bold text-purple-600 mb-2">Track</p>
                <p className="text-slate-700">All Your Deals</p>
                <p className="text-sm text-slate-600 mt-2">Real-time analytics dashboard</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-slate-900 to-slate-800 text-white p-16 rounded-sm">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Trading?</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Join Tanzania's premier mineral trading platform. Transparent, secure, regulated.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/auth')}
            className="bg-amber-600 hover:bg-amber-700 text-white px-12 py-6 text-xl font-bold rounded-sm"
          >
            Create Account Now
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
}